import React, { useState, useCallback, useEffect, useContext, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import { useHistory, useRouteMatch } from 'react-router-dom';

import { TreeNav } from 'ui-components/components/tree-navigation';
import { noop } from 'ui-components/default-props/noop';

import DataDrivenLogo from '../../../components/data-driven-logo';
import { SearchForm } from '../../../components/orders-grid/search-form';
import { useEnableExternalInventory } from '../../../feature-flags/hooks/use-enable-external-inventory';
import { useEnablePurchaseOrders } from '../../../feature-flags/hooks/use-enable-purchase-orders';
import { useInventoryManagementBasicFeatureGate } from '../../../feature-gates/use-inventory-management-basic-feature-gate';
import { useProductCatalogFeatureGate } from '../../../feature-gates/use-product-catalog-feature-gate';
import { useQuickSearchTerm } from '../../../hooks/routing/use-quick-search-term';
import { BASE_PRODUCTS_PATH, INVENTORY_WAREHOUSE_PATH, EXTERNAL_INVENTORY_SOURCE_PATH } from '../../../routes/constants';
import Global from '../../../store/global-selectors';
import messages from '../messages';
import { QuickSearchContext } from '../quicksearch-context';
import { useExternalInventoryConnectionsContext } from '../external-inventory-connections-context';
import style from './left-nav-container.module.less';
import { useEnableTransferOrders } from '../../../feature-flags/hooks/use-enable-transfer-orders';
import { useEnableSuppliersGrid } from '../../../feature-flags/hooks/use-enable-suppliers-grid';

const useInventoryNav = (searchPerformed, { productsCount = 0, inventoryCount = 0, externalInventoryCount = 0 }) => {
  // keep track of location so we know to expand the inventory section
  const history = useHistory();
  const inventoryWarehouses = useSelector(Global.selectSellerInventoryWarehouses);
  const warehouseMatch = useRouteMatch(`${BASE_PRODUCTS_PATH}/${INVENTORY_WAREHOUSE_PATH}`);
  const selectedInventoryWarehouseId = warehouseMatch?.params?.inventoryWarehouseId ?? undefined;
  const externalInventoryMatch = useRouteMatch(`${BASE_PRODUCTS_PATH}/${EXTERNAL_INVENTORY_SOURCE_PATH}`);
  const selectedExternalInventoryMatch = externalInventoryMatch?.params?.externalInventorySourceId ?? undefined;
  const isPurchaseOrdersFFEnabled = useEnablePurchaseOrders();
  const isTransferOrdersFFEnabled = useEnableTransferOrders();
  const isExternalInventoryFFEnabled = useEnableExternalInventory();
  const isSuppliersFFEnabled = useEnableSuppliersGrid();
  const { shouldHide: shouldHideProductsLinks } = useProductCatalogFeatureGate();
  const { shouldHide: shouldHideInventoryLinks } = useInventoryManagementBasicFeatureGate();
  const marketplaces = useSelector(Global.selectReferenceDatagetMarketplaces);
  const fulfillmentProviders = useSelector(Global.selectReferenceDatagetFulfillmentProviders);
  const { connectionSources } = useExternalInventoryConnectionsContext();
  const CONNECTION_TYPE_SHIPENGINE = 'shipengine';

  const getLogoPropsForConnectionType = useCallback(
    (connectionType) => {
      const marketplace = marketplaces.find((m) => m.apiCode?.toLowerCase() === connectionType?.toLowerCase());
      if (marketplace) {
        return { marketplaceId: marketplace.marketplaceId };
      }

      const fulfillmentProvider = fulfillmentProviders.find((fp) => fp.apiCode?.toLowerCase() === connectionType?.toLowerCase());
      if (fulfillmentProvider) {
        return { fillProviderId: fulfillmentProvider.fillProviderId };
      }

      return { marketplaceId: 0 };
    },
    [marketplaces, fulfillmentProviders],
  );

  const inventoryNavItems = useMemo(() => {
    if (shouldHideInventoryLinks || inventoryWarehouses.length === 0) {
      return undefined;
    }

    const warehouseItems = inventoryWarehouses.map(({ inventoryWarehouseId, name }) => ({
      id: `inventory/warehouse/${inventoryWarehouseId}`,
      selected: inventoryWarehouseId === selectedInventoryWarehouseId,
      label: name,
      theme: 'light',
      shouldRenderAnIcon: true,
      customIcon: (
        <DataDrivenLogo className={style['indented-item']} logoType='icon' {...getLogoPropsForConnectionType(CONNECTION_TYPE_SHIPENGINE)} />
      ),
    }));

    return [...warehouseItems];
  }, [inventoryWarehouses, selectedInventoryWarehouseId, shouldHideInventoryLinks, getLogoPropsForConnectionType]);

  const externalInventoryNavItems = useMemo(() => {
    if (shouldHideInventoryLinks || !connectionSources?.length) {
      return undefined;
    }

    const connectionItems = (connectionSources ?? []).map(({ connectionId, name, connectionType }) => ({
      id: `externalinv/${connectionId}`,
      selected: connectionId === selectedExternalInventoryMatch,
      label: `${name}`,
      theme: 'light',
      shouldRenderAnIcon: true,
      customIcon: <DataDrivenLogo className={style['indented-item']} logoType='icon' {...getLogoPropsForConnectionType(connectionType)} />,
    }));

    return [...connectionItems];
  }, [shouldHideInventoryLinks, connectionSources, selectedExternalInventoryMatch, getLogoPropsForConnectionType]);

  // todo: Purchase orders should eventually be its own feature, but for now we can drive it off of the inventory management feature.
  const isPurchaseOrdersHidden = !isPurchaseOrdersFFEnabled || shouldHideInventoryLinks;
  const isExternalInventoryHidden = !isExternalInventoryFFEnabled || shouldHideInventoryLinks;
  const isTransferOrdersHidden = !isTransferOrdersFFEnabled || shouldHideInventoryLinks;
  const isSuppliersHidden = !isSuppliersFFEnabled || shouldHideInventoryLinks;

  return useMemo(() => {
    const path = history.location.pathname;
    const isOnInventoryPath = path.includes('inventory');
    const isOnExternalInventoryPath = path.includes('externalinv');

    const isInventoryItemsExpanded =
      (isOnExternalInventoryPath && inventoryNavItems?.length > 0 && externalInventoryNavItems?.length > 0) ||
      (isOnInventoryPath && inventoryNavItems?.length > 0);

    const isExternalItemsExpanded =
      (isOnInventoryPath && inventoryNavItems?.length > 0 && externalInventoryNavItems?.length > 0) ||
      (isOnExternalInventoryPath && externalInventoryNavItems?.length > 0);

    return [
      {
        id: '',
        selected: path === '/products',
        label: <FormattedMessage {...messages.products} />,
        count: searchPerformed ? productsCount : undefined,
        isHidden: shouldHideProductsLinks,
      },
      {
        id: 'producttype',
        selected: path.indexOf('producttype') > 0,
        label: <FormattedMessage {...messages.presetGroups} />,
        isHidden: shouldHideProductsLinks,
      },
      {
        id: 'categories',
        selected: path.indexOf('categories') > 0,
        label: <FormattedMessage {...messages.reportingCategories} />,
        isHidden: shouldHideProductsLinks,
      },
      {
        id: 'inventory',
        selected: path === '/products/inventory',
        label: <FormattedMessage {...messages.inventory} />,
        count: searchPerformed ? inventoryCount : undefined,
        items: inventoryNavItems,
        expanded: isInventoryItemsExpanded,
        isHidden: shouldHideInventoryLinks,
      },
      {
        id: 'externalinv',
        selected: path === '/products/externalinv',
        label: <FormattedMessage {...messages.externalinventory} />,
        count: searchPerformed ? externalInventoryCount : undefined,
        items: externalInventoryNavItems,
        expanded: isExternalItemsExpanded,
        isHidden: isExternalInventoryHidden,
      },
      {
        id: 'purchaseorders',
        selected: path.indexOf('purchaseorders') > 0,
        label: <FormattedMessage {...messages.purchaseOrders} />,
        isHidden: isPurchaseOrdersHidden,
      },
      {
        id: 'suppliers',
        selected: path.indexOf('suppliers') > 0,
        label: <FormattedMessage {...messages.suppliers} />,
        isHidden: isSuppliersHidden,
      },
      {
        id: 'transferorders',
        selected: path.indexOf('transferorders') > 0,
        label: <FormattedMessage {...messages.transferOrders} />,
        isHidden: isTransferOrdersHidden,
      },
    ];
  }, [
    history.location.pathname,
    inventoryNavItems,
    externalInventoryNavItems,
    searchPerformed,
    productsCount,
    shouldHideProductsLinks,
    inventoryCount,
    shouldHideInventoryLinks,
    externalInventoryCount,
    isExternalInventoryHidden,
    isPurchaseOrdersHidden,
    isTransferOrdersHidden,
    isSuppliersHidden,
  ]);
};

const ProductsLeftNav = () => {
  const searchQuery = useQuickSearchTerm() || '';
  const [searchTerm, setSearchTerm] = useState(searchQuery);
  const [searchPerformed, setSearchPerformed] = useState(!!searchTerm);
  const { quickSearch, clearQuickSearch, onSubmitQuickSearch } = useContext(QuickSearchContext);
  const navItems = useInventoryNav(searchPerformed, quickSearch);

  const filteredViews = useMemo(() => {
    return navItems.filter((view) => !view.isHidden);
  }, [navItems]);

  useEffect(() => {
    setSearchTerm(searchQuery);
    setSearchPerformed(!!searchQuery);
  }, [searchQuery]);

  const buildTagProps = useCallback(
    ({ id }) => {
      let query = `?quickSearch=${searchQuery}`;
      if (!searchPerformed || id === 'categories' || id === 'producttype') {
        query = '';
      }
      const to = id.length ? `${BASE_PRODUCTS_PATH}/${id}${query}` : `${BASE_PRODUCTS_PATH}${query}`;
      return {
        to,
      };
    },
    [searchPerformed, searchQuery],
  );
  const onSearch = useCallback(() => {
    onSubmitQuickSearch(searchTerm);
  }, [searchTerm, onSubmitQuickSearch]);

  const onItemSelected = useCallback(
    ({ id }) => {
      if (searchPerformed && (id === 'categories' || id === 'producttype')) {
        clearQuickSearch();
      }
    },
    [searchPerformed, clearQuickSearch],
  );

  return (
    <div className={style['tree-nav-container']}>
      <SearchForm
        clearSearchInput={clearQuickSearch}
        defaultFormData={{ searchTerm }}
        getSearchTermCount={noop}
        handleSearchInputChange={setSearchTerm}
        isHighlighted={searchPerformed}
        onSearch={onSearch}
        placeholder={messages.searchValue}
        refreshGridData={noop}
      />
      <div className={style['tree-and-batches']}>
        <TreeNav
          buildTagProps={buildTagProps}
          isHighlighted={searchPerformed}
          items={filteredViews}
          onExpandClick={noop}
          onItemSelected={onItemSelected}
        />
      </div>
    </div>
  );
};

export default ProductsLeftNav;
